import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import correspondenceService from '../services/correspondenceService';
import { extractErrorMessage } from '../../../utils/errorUtils';

const typeLabels = {
  creation: 'Creacion',
  state_change: 'Estado',
  assignment: 'Lectura',
  thread: 'Hilo',
  document: 'Documento',
  signature: 'Firma',
  email: 'Email',
  read: 'Lectura',
};

const formatTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('es-CO', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const EventDetails = ({ event }) => {
  const { type, details } = event;

  if (type === 'assignment' && details?.assignedTo) {
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="body2">
          Dirigido a {details.assignedTo.name || details.assignedTo.email}
        </Typography>
        {details.read ? (
          <Chip label={`Leida: ${formatDate(details.readAt)}`} size="small" variant="outlined" sx={{ mt: 0.5, borderColor: 'rgba(29,31,32,0.16)' }} />
        ) : (
          <Chip label="No leida" size="small" variant="outlined" sx={{ mt: 0.5, borderColor: 'rgba(29,31,32,0.16)' }} />
        )}
      </Box>
    );
  }

  if (type === 'thread' && details) {
    return (
      <Box sx={{ mt: 0.5 }}>
        {details.taggedUsers?.length > 0 && (
          <Typography variant="body2">
            Etiquetados: {details.taggedUsers.length} usuario(s)
          </Typography>
        )}
      </Box>
    );
  }

  if (type === 'document' && details) {
    return (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {details.medium === 'digital' ? 'Digital' : details.medium || ''}
      </Typography>
    );
  }

  if (type === 'email' && details) {
    return (
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="body2">
          Para: {details.recipientEmail || details.recipientName}
        </Typography>
        {details.opened && (
          <Chip label={`Abierto: ${formatDate(details.openedAt)}`} size="small" variant="outlined" sx={{ mt: 0.5, borderColor: 'rgba(29,31,32,0.16)' }} />
        )}
      </Box>
    );
  }

  if (type === 'state_change' && details) {
    return (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {details.from || '(ninguno)'} &rarr; {details.to}
      </Typography>
    );
  }

  if (type === 'signature' && details) {
    return (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Token: {details.signatureToken?.slice(0, 16)}...
      </Typography>
    );
  }

  return null;
};

const TimelineEvent = ({ event, isLast }) => {
  const label = typeLabels[event.type] || 'Evento';

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative', ml: 1 }}>
      {/* Timeline marker + line */}
      <Box sx={{ position: 'relative', width: 9, flexShrink: 0 }}>
        <Box sx={{ position: 'absolute', left: 0, top: 18, width: 9, height: 9, bgcolor: '#5980a6' }} />
        {!isLast && (
          <Box sx={{ position: 'absolute', left: 4, top: 27, bottom: -12, width: '1px', bgcolor: 'rgba(29,31,32,0.16)' }} />
        )}
      </Box>

      {/* Content card */}
      <Paper variant="outlined" sx={{ flex: 1, p: 2, mb: 1.5, bgcolor: '#f2f2f3' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{event.description}</Typography>
            <Chip
              label={label}
              size="small"
              variant="outlined"
              sx={{ borderColor: 'rgba(29,31,32,0.16)', color: '#5980a6', fontSize: '0.6875rem' }}
            />
          </Box>
          <Typography sx={{ fontSize: '0.875rem', fontFamily: 'monospace', color: 'rgba(29,31,32,0.55)', flexShrink: 0 }}>
            {formatTime(event.date)}
          </Typography>
        </Box>

        {event.actor && (
          <Typography variant="body2" sx={{ color: 'rgba(29,31,32,0.55)', mt: 0.5 }}>
            {event.actor.name}{event.actor.email ? ` (${event.actor.email})` : ''}
          </Typography>
        )}

        <EventDetails event={event} />
      </Paper>
    </Box>
  );
};

const CorrespondenceTraceability = ({ correspondenceId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await correspondenceService.getTraceability(correspondenceId);
        setEvents(res.data || []);
      } catch (err) {
        setError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [correspondenceId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (events.length === 0) {
    return <Alert severity="info">No se encontraron eventos de trazabilidad.</Alert>;
  }

  // Group events by date
  const grouped = {};
  for (const event of events) {
    const dateKey = new Date(event.date).toLocaleDateString('es-CO', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(event);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h2">Trazabilidad</Typography>
        <Chip label={`${events.length} eventos`} size="small" variant="outlined" sx={{ borderColor: 'rgba(29,31,32,0.16)' }} />
      </Box>

      {Object.entries(grouped).map(([dateLabel, dayEvents]) => (
        <Box key={dateLabel} sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'rgba(29,31,32,0.55)',
              mb: 1.5,
            }}
          >
            {dateLabel}
          </Typography>
          {dayEvents.map((event, i) => (
            <TimelineEvent key={`${event.type}-${event.date}-${i}`} event={event} isLast={i === dayEvents.length - 1} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

export default CorrespondenceTraceability;
